import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Award } from '../../../types/award';
import { awardService } from '../../../services/awardService';
import { supabase } from '../../../lib/supabase';
import { Button } from '../../ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../ui/form';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Select } from '../../ui/select';
import { useToast } from '../../ui/use-toast';
import { format } from 'date-fns';
import { Checkbox } from '../../ui/checkbox';
import { Card, CardContent } from '../../ui/card';
import { X, ArrowLeft as ArrowLeftIcon, ArrowRight as ArrowRightIcon, Loader2 } from 'lucide-react';

interface Contest {
  id: string;
  name: string;
  slug: string;
  end_date: string;
  status: string;
}

interface FormData {
  name: string;
  description: string;
  logo_url: string;
  partner_logos: string[];
  start_date: string;
  end_date: string;
  status: 'active' | 'upcoming' | 'ended';
  selectedContests: string[];
}

interface AwardFormProps {
  slug?: string;
}

const statusOptions = [
  { value: 'upcoming', label: 'À venir' },
  { value: 'active', label: 'Actif' },
  { value: 'ended', label: 'Terminé' }
];

export const AwardForm = ({ slug }: AwardFormProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [contests, setContests] = useState<Contest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [awardId, setAwardId] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [partnerLogoInput, setPartnerLogoInput] = useState('');
  const [partnerLogoError, setPartnerLogoError] = useState<string | null>(null);
  const [uploadingPartnerLogo, setUploadingPartnerLogo] = useState(false);

  const form = useForm<FormData>({
    defaultValues: {
      name: '',
      description: '',
      logo_url: '',
      partner_logos: [],
      start_date: format(new Date(), 'yyyy-MM-dd'),
      end_date: format(new Date(), 'yyyy-MM-dd'),
      status: 'upcoming',
      selectedContests: [],
    },
  });

  const fetchContests = async () => {
    try {
      const { data, error } = await supabase
        .from('contests')
        .select('id, name, slug, end_date, status')
        .order('name');

      if (error) throw error;
      // Exclure les concours terminés (status = "finished" OU end_date < aujourd'hui)
      const now = new Date();
      const filtered = (data || []).filter((c: any) => {
        if (c.status && c.status.toLowerCase() === 'finished') return false;
        if (c.end_date && new Date(c.end_date) < now) return false;
        return true;
      });
      setContests(filtered);
    } catch (error) {
      console.error('Erreur lors de la récupération des concours:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger la liste des concours',
        variant: 'destructive',
      });
    }
  };

  const fetchAward = async () => {
    if (!slug) return;

    try {
      setIsLoading(true);
      const award = await awardService.getAwardBySlug(slug);
      if (!award) throw new Error('Award non trouvé');

      // Stocker l'ID de l'award pour l'utiliser lors de la mise à jour
      setAwardId(award.id);

      form.reset({
        name: award.name,
        description: award.description,
        logo_url: award.logo_url,
        partner_logos: award.partner_logos || [],
        start_date: format(new Date(award.start_date), 'yyyy-MM-dd'),
        end_date: format(new Date(award.end_date), 'yyyy-MM-dd'),
        status: award.status,
        selectedContests: award.award_categories?.map(cat => cat.contest?.id || '') || [],
      });

      if (award.logo_url) {
        setImagePreview(award.logo_url);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'award:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger l\'award',
        variant: 'destructive',
      });
      navigate('/admin/awards');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContests();
    if (slug) {
      fetchAward();
    }
  }, [slug]);

  // Surveiller les changements d'URL du logo pour mettre à jour l'aperçu
  useEffect(() => {
    const logoUrl = form.watch('logo_url');
    if (logoUrl && logoUrl !== imagePreview) {
      setImagePreview(logoUrl);
    }
  }, [form.watch('logo_url')]);

  const onSubmit = async (data: FormData) => {
    try {
      setIsLoading(true);

      if (slug) {
        // Mise à jour
        await awardService.updateAward(awardId!, {
          name: data.name,
          description: data.description,
          logo_url: data.logo_url,
          partner_logos: data.partner_logos,
          start_date: data.start_date,
          end_date: data.end_date,
          status: data.status,
        });

        // Mettre à jour les catégories
        const currentCategories = await awardService.getAwardById(awardId!);
        if (currentCategories?.award_categories) {
          for (const cat of currentCategories.award_categories) {
            await awardService.removeCategory(cat.id);
          }
        }

        // Ajouter les nouvelles catégories
        for (const contestId of data.selectedContests) {
          const contest = contests.find(c => c.id === contestId);
          if (contest) {
            await awardService.addCategory(awardId!, contestId, contest.name);
          }
        }
      } else {
        // Création
        const award = await awardService.createAward({
          name: data.name,
          description: data.description,
          logo_url: data.logo_url,
          partner_logos: data.partner_logos,
          start_date: data.start_date,
          end_date: data.end_date,
          status: data.status,
        });

        // Ajouter les catégories
        for (const contestId of data.selectedContests) {
          const contest = contests.find(c => c.id === contestId);
          if (contest) {
            await awardService.addCategory(award.id, contestId, contest.name);
          }
        }
      }

      toast({
        title: 'Succès',
        description: `L'award a été ${slug ? 'modifié' : 'créé'} avec succès`,
      });
      navigate('/admin/awards');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast({
        title: 'Erreur',
        description: `Impossible de ${slug ? 'modifier' : 'créer'} l'award`,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePartnerLogoFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    setUploadingPartnerLogo(true);
    setPartnerLogoError(null);
    try {
      const uploadedUrls: string[] = [];
      for (const file of Array.from(e.target.files)) {
        // Validation type/size si besoin
        if (!file.type.startsWith('image/')) {
          setPartnerLogoError('Seuls les fichiers images sont acceptés.');
          continue;
        }
        if (file.size > 2 * 1024 * 1024) { // 2 Mo max
          setPartnerLogoError('Chaque logo doit faire moins de 2 Mo.');
          continue;
        }
        const filePath = `awards/${Date.now()}-${file.name}`;
        const { error } = await supabase.storage.from('partners').upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });
        if (error) {
          setPartnerLogoError(`Erreur lors de l'upload: ${error.message}`);
          continue;
        }
        const { data } = supabase.storage.from('partners').getPublicUrl(filePath);
        if (data && data.publicUrl) {
          uploadedUrls.push(data.publicUrl);
        }
      }
      if (uploadedUrls.length) {
        const currentLogos = form.getValues('partner_logos') || [];
        form.setValue('partner_logos', [...currentLogos, ...uploadedUrls]);
      }
    } catch (error: any) {
      setPartnerLogoError('Erreur lors de l\'upload des logos des partenaires');
      console.error(error);
    } finally {
      setUploadingPartnerLogo(false);
    }
  };

  const addPartnerLogo = () => {
    if (!partnerLogoInput || !partnerLogoInput.trim()) return;

    const currentLogos = form.getValues('partner_logos') || [];
    form.setValue('partner_logos', [...currentLogos, partnerLogoInput.trim()]);
    setPartnerLogoInput('');
  };

  const removePartnerLogo = (index: number) => {
    const currentLogos = form.getValues('partner_logos') || [];
    form.setValue('partner_logos', currentLogos.filter((_, i) => i !== index));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 text-gray-100">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-200">Nom de l'award</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Ex: Miss Mum 2025" className="bg-black/70 border-gray-700 text-gray-100 focus:border-yellow-400" />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-200">Description</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Description de l'award..."
                  rows={4}
                  className="bg-black/70 border-gray-700 text-gray-100 focus:border-yellow-400"
                />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>
          )}
        />

        <Card className="bg-black/70 border-gray-700 rounded-xl overflow-hidden">
          <CardContent className="pt-6">
            <h3 className="text-lg font-medium mb-4 text-gray-200">Logo de l'award</h3>
            <FormField
              control={form.control}
              name="logo_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-gray-200">URL du logo</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      placeholder="https://..." 
                      className="bg-black/70 border-gray-700 text-gray-100 focus:border-yellow-400" 
                    />
                  </FormControl>
                  <FormMessage className="text-red-400" />
                </FormItem>
              )}
            />
            
            {imagePreview && (
              <div className="mt-4 relative aspect-square w-40 rounded-xl overflow-hidden border border-white/10">
                <img
                  src={imagePreview}
                  alt="Aperçu du logo"
                  className="object-cover w-full h-full"
                />
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-2">
          <label className="block text-gray-200 font-semibold mb-1">Logos des partenaires</label>
          <div className="flex gap-2 mb-2">
            <input
              type="file"
              accept="image/*"
              multiple
              className="bg-black/70 border-gray-700 text-gray-100 focus:border-yellow-400 rounded p-1"
              onChange={handlePartnerLogoFiles}
            />
            <input
              type="text"
              placeholder="Collez des URLs, séparées par des virgules"
              className="bg-black/70 border-gray-700 text-gray-100 focus:border-yellow-400 rounded p-1 flex-1"
              value={partnerLogoInput}
              onChange={e => setPartnerLogoInput(e.target.value)}
            />
            <Button type="button" onClick={addPartnerLogo} disabled={uploadingPartnerLogo}>
              {uploadingPartnerLogo ? <Loader2 className="animate-spin h-4 w-4" /> : 'Ajouter'}
            </Button>
          </div>
          {partnerLogoError && (
            <div className="text-red-400 text-xs mb-2">{partnerLogoError}</div>
          )}
          {(form.watch('partner_logos') || []).length > 0 ? (
            <div className="flex flex-wrap gap-4">
              {form.watch('partner_logos').map((logo: string, idx: number, arr: string[]) => (
                <div key={logo + idx} className="relative w-24 h-24 border border-white/10 rounded-xl overflow-hidden bg-white/10 flex items-center justify-center group">
                  <img
                    src={logo}
                    alt={`Logo du partenaire ${idx + 1}`}
                    className="object-cover w-full h-full"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 p-2 bg-red-500/80 hover:bg-red-500 rounded-xl text-white transition-colors opacity-0 group-hover:opacity-100"
                    onClick={() => removePartnerLogo(idx)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  {/* Boutons de réorganisation */}
                  <div className="absolute bottom-1 left-1 flex gap-1 opacity-80 group-hover:opacity-100">
                    <button
                      type="button"
                      disabled={idx === 0}
                      onClick={() => {
                        const logos = [...form.getValues('partner_logos')];
                        if (idx > 0) {
                          [logos[idx - 1], logos[idx]] = [logos[idx], logos[idx - 1]];
                          form.setValue('partner_logos', logos);
                        }
                      }}
                      className={`bg-gray-700/80 hover:bg-yellow-400 text-white hover:text-black rounded p-1 ${idx === 0 ? 'opacity-40 cursor-not-allowed' : ''}`}
                      title="Monter"
                    >
                      <ArrowLeftIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      disabled={idx === arr.length - 1}
                      onClick={() => {
                        const logos = [...form.getValues('partner_logos')];
                        if (idx < logos.length - 1) {
                          [logos[idx + 1], logos[idx]] = [logos[idx], logos[idx + 1]];
                          form.setValue('partner_logos', logos);
                        }
                      }}
                      className={`bg-gray-700/80 hover:bg-yellow-400 text-white hover:text-black rounded p-1 ${idx === arr.length - 1 ? 'opacity-40 cursor-not-allowed' : ''}`}
                      title="Descendre"
                    >
                      <ArrowRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm">Aucun logo de partenaire ajouté</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="start_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-gray-200">Date de début</FormLabel>
                <FormControl>
                  <Input type="date" {...field} className="bg-black/70 border-gray-700 text-gray-100 focus:border-yellow-400" />
                </FormControl>
                <FormMessage className="text-red-400" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="end_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-gray-200">Date de fin</FormLabel>
                <FormControl>
                  <Input type="date" {...field} className="bg-black/70 border-gray-700 text-gray-100 focus:border-yellow-400" />
                </FormControl>
                <FormMessage className="text-red-400" />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-200">Statut</FormLabel>
              <FormControl>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={statusOptions}
                  className="bg-black/70 border-gray-700 text-gray-100 focus:border-yellow-400"
                />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>
          )}
        />

        <div className="space-y-4">
          <FormField
            control={form.control}
            name="selectedContests"
            render={() => (
              <FormItem>
                <div className="mb-4">
                  <FormLabel className="text-gray-200">Sélectionner les concours</FormLabel>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {contests.map((contest) => (
                    <div key={contest.id} className="flex items-start space-x-3 space-y-0">
                      <Checkbox
                        checked={form.watch('selectedContests').includes(contest.id)}
                        onCheckedChange={(checked) => {
                           const currentValue = form.getValues('selectedContests');
                          const newValue = checked
                            ? [...currentValue, contest.id]
                            : currentValue.filter((id) => id !== contest.id);
                          form.setValue('selectedContests', newValue);
                        }}
                        className="border-gray-600 text-yellow-400"
                      />
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-gray-200">{contest.name}</FormLabel>
                      </div>
                    </div>
                  ))}
                </div>
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/awards')}
            className="border-gray-600 text-gray-200 hover:bg-gray-800"
          >
            Annuler
          </Button>
          <Button type="submit" disabled={isLoading} className="bg-yellow-500 hover:bg-yellow-600 text-black">
            {slug ? 'Modifier' : 'Créer'} l'award
          </Button>
        </div>
      </form>
    </Form>
  );
};
